import { useQuery } from "@tanstack/react-query";
import {
  getDatabricksJobTaskRuns,
  getDatabricksJobTaskRunsDetail,
  getDatabricksJobTasksInventory,
  getDatabricksSparkAnalysis,
  getDatabricksSparkOpportunities,
  getDatabricksStageMetricsPaginated,
  getDatabricksSparkTasksPaginated,
  getDatabricksTaskRunsGraph,
  getDatabricksTaskStats,
} from "@/helpers/apis";

export const useDatabricksJobTasksInventory = (
  jobId: string,
  instanceId: number | string | undefined,
  enabled = true
) => {
  return useQuery({
    queryKey: ["databricks-job-tasks-inventory", jobId, instanceId],
    queryFn: () => getDatabricksJobTasksInventory(jobId, { instance_id: instanceId ?? "" }),
    enabled: enabled && Boolean(jobId) && instanceId !== undefined && instanceId !== "",
    retry: false,
  });
};

export const useDatabricksJobTaskRuns = (
  jobId: string,
  params: {
    start_date: string;
    end_date?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-job-task-runs",
      jobId,
      params.start_date,
      params.end_date,
      params.sortAttribute,
      params.sortOrder,
      params.page,
      params.page_size,
    ],
    queryFn: () => getDatabricksJobTaskRuns(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};

// Run- or task-scoped variant. Exactly one of `run_id` / `task_key` must
// be set; the hook short-circuits to disabled when neither is provided so
// we never fire a 400 from the BE.
export const useDatabricksJobTaskRunsDetail = (
  jobId: string,
  params: {
    start_date: string;
    end_date?: string;
    run_id?: string;
    task_key?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-job-task-runs-detail",
      jobId,
      params.start_date,
      params.end_date,
      params.run_id,
      params.task_key,
      params.sortAttribute,
      params.sortOrder,
      params.page,
      params.page_size,
    ],
    queryFn: () => getDatabricksJobTaskRunsDetail(jobId, params),
    enabled: enabled && Boolean(jobId) && Boolean(params.run_id || params.task_key),
  });
};

export const useDatabricksTaskStats = (
  jobId: string,
  taskKey: string,
  params: { start_date: string; end_date?: string },
  enabled = true
) => {
  return useQuery({
    queryKey: ["databricks-task-stats", jobId, taskKey, params.start_date, params.end_date],
    queryFn: () => getDatabricksTaskStats(jobId, taskKey, params),
    enabled: enabled && Boolean(jobId && taskKey),
  });
};

export const useDatabricksTaskRunsGraph = (
  jobId: string,
  taskKey: string,
  params: { start_date: string; end_date?: string },
  enabled = true
) => {
  return useQuery({
    queryKey: ["databricks-task-runs-graph", jobId, taskKey, params.start_date, params.end_date],
    queryFn: () => getDatabricksTaskRunsGraph(jobId, taskKey, params),
    enabled: enabled && Boolean(jobId && taskKey),
  });
};

export const useDatabricksSparkAnalysis = (
  jobId: string,
  params: { run_id?: string; task_key?: string } = {},
  enabled = true
) => {
  return useQuery({
    queryKey: ["databricks-spark-analysis", jobId, params.run_id, params.task_key],
    queryFn: () => getDatabricksSparkAnalysis(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};

export const useDatabricksSparkOpportunities = (
  jobId: string,
  params: { cluster_id?: string } = {},
  enabled = true
) => {
  return useQuery({
    queryKey: ["databricks-spark-opportunities", jobId, params.cluster_id],
    queryFn: () => getDatabricksSparkOpportunities(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};

export const useDatabricksStageMetrics = (
  jobId: string,
  params: {
    run_id?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
    stage_ids?: string;
  } = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-stage-metrics",
      jobId,
      params.run_id,
      params.sortAttribute,
      params.sortOrder,
      params.page,
      params.page_size,
      params.stage_ids,
    ],
    queryFn: () => getDatabricksStageMetricsPaginated(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};

export const useDatabricksSparkTasks = (
  jobId: string,
  params: {
    run_id?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
    stage_ids?: string;
  } = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-spark-tasks",
      jobId,
      params.run_id,
      params.sortAttribute,
      params.sortOrder,
      params.page,
      params.page_size,
      params.stage_ids,
    ],
    queryFn: () => getDatabricksSparkTasksPaginated(jobId, params),
    enabled: enabled && Boolean(jobId),
  });
};
